import { CertificationEvaluationEvent, PnsEventType } from '../models/pns-event';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { CertificationEvaluationEventService } from '../services/events.service';
import { computed, effect, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, pipe, switchMap, tap } from 'rxjs';
import { PaginatedResponse } from '../models/pageable';
import { DateUtils } from '../shared/utils/date.utils';
import { removeEmptyValues } from '../shared/utils/emptyValues.utils';

export interface FiltersState {
  textSearch: string | null;
  type: PnsEventType | null;
  executedAt: DateRange | null;
  referenceName: string | null;
  userEmail: string | null;
}

export interface DateRange {
  from: string | null;
  to: string | null;
}

export interface EventsState {
  events: CertificationEvaluationEvent[];
  selectedEvent: CertificationEvaluationEvent | null;
  isLoading: boolean;
  error: string | null;
  filters: FiltersState;
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  sort: string;
}

export const initialEventsState: EventsState = {
  events: [],
  selectedEvent: null,
  isLoading: false,
  error: null,
  filters: {
    textSearch: null,
    type: null,
    executedAt: null,
    referenceName: null,
    userEmail: null,
  },
  page: 0,
  size: 15,
  totalElements: 0,
  totalPages: 0,
  sort: 'executedAt,desc',
};

export const EventsStore = signalStore(
  { providedIn: 'root' },
  withState<EventsState>(initialEventsState),
  withMethods((store, eventsService = inject(CertificationEvaluationEventService)) => ({
    updateFilters(updatedFilters: Partial<FiltersState>): void {
      const formattedFilters = { ...updatedFilters };
      if ('executedAt' in updatedFilters) {
        const executedAt = updatedFilters.executedAt;
        formattedFilters.executedAt = executedAt
          ? {
              from: DateUtils.toApiFormat(formattedFilters.executedAt?.from, 'start'),
              to: DateUtils.toApiFormat(formattedFilters.executedAt?.to, 'end'),
            }
          : null;
      }

      patchState(store, (state) => ({
        filters: { ...state.filters, ...formattedFilters },
        page: 0,
      }));
    },

    goToPage(page: number) {
      patchState(store, { page });
    },

    updateSort(sort: { active: string; direction: 'asc' | 'desc' | '' }): void {
      const sortString = sort.direction
        ? `${sort.active},${sort.direction}`
        : initialEventsState.sort;
      patchState(store, { sort: sortString });
    },

    resetFilters() {
      patchState(store, {
        filters: initialEventsState.filters,
        page: 0,
      });
    },

    loadEvents: rxMethod<void>(
      pipe(
        debounceTime(300),
        switchMap(() => {
          patchState(store, { isLoading: true });

          const pageRequest = {
            page: store.page(),
            size: store.size(),
            sort: [store.sort()],
          };
          const currentFilters = store.filters();

          const activeFilters = Object.fromEntries(
            Object.entries(currentFilters).filter(([_, value]) => value != null && value != ''),
          );

          return eventsService.getEvents(pageRequest, activeFilters).pipe(
            tap({
              next: (response: PaginatedResponse<CertificationEvaluationEvent>) => {
                patchState(store, {
                  events: response?.content ?? [],
                  totalElements: response?.totalElements ?? 0,
                  totalPages: response?.totalPages ?? 0,
                  isLoading: false,
                  error: null,
                });
              },
              error: (err) => {
                patchState(store, {
                  error: 'Failed to load events',
                  isLoading: false,
                  events: [],
                });
                console.error(err);
              },
            }),
          );
        }),
      ),
    ),
  })),

  withComputed(({ filters, page, totalPages }) => ({
    isAnyFilterActive: computed(() => {
      const currentFilters = filters();

      const allFilterValues = [
        currentFilters.textSearch,
        currentFilters.type,
        currentFilters.executedAt?.from,
        currentFilters.executedAt?.to,
        currentFilters.referenceName,
        currentFilters.userEmail,
      ];

      return allFilterValues.some((value) => value != null && value !== '');
    }),
    isFirstPage: computed(() => page() === 0),
    isLastPage: computed(() => page() >= totalPages() - 1),
    activeFilters: computed(() => {
      const currentFilters = filters();
      const executedAtValue = currentFilters.executedAt;

      const activeFiltersObj: FiltersState = {
        textSearch: currentFilters.textSearch,
        type: currentFilters.type,
        executedAt: executedAtValue
          ? { from: executedAtValue.from ?? null, to: executedAtValue.to ?? null }
          : null,
        referenceName: currentFilters.referenceName,
        userEmail: currentFilters.userEmail,
      };
      return removeEmptyValues(activeFiltersObj);
    }),
  })),
);
